import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { SignupInProgressStep } from '../../controllers/SignupPlanViewController';
import { SignupPlan, SignupPlanNotice } from '../../types';
import { SignupStepUnavailable } from '../SignupStepUnavailable';
import { noticeSx } from '../../helpers/sx';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

// Above the list rather than on a screen of its own: choosing a plan is what starting again means, so a
// dedicated screen would only hold a button leading back here.
const NOTICE_TEXT: Record<SignupPlanNotice, string> = {
  expired: 'Your earlier signup expired before it was finished, so we have let it go. Choosing a plan starts a new one — nothing was charged.',
  linkDead: 'That link no longer works. Choosing a plan starts a new signup.',
};

const IN_PROGRESS_TEXT: Record<SignupInProgressStep, string> = {
  account: 'You have a signup in progress — your account details were never finished.',
  verify_email: 'You have a signup in progress — we are waiting for the code we emailed you. The screen it opens is also where a mistyped address is corrected.',
  company_info: 'You have a signup in progress — your email is confirmed and your company details are all that is left.',
};

/**
 * Said rather than offered as a button of its own: choosing a plan already starts a fresh funnel and leaves
 * the old one where it stands, so a control beside it would be the same act under another name.
 */
const START_OVER_TEXT = ' Choosing a plan below starts a new signup instead.';

type Props = {
  plans: SignupPlan[];
  localNumberPriceUsd: number;
  loading: boolean;
  error: string | null;
  /** Why a client who already had a funnel is being shown this list, or null on an ordinary first visit. */
  notice: SignupPlanNotice | null;
  /** The step an unfinished funnel in this browser stopped on, or null when there is none to offer. */
  inProgress: SignupInProgressStep | null;
  startingPlanId: string | null;
  /** The funnel behind the stored token could not be read, so this list is not this visitor's to be offered. */
  unavailable: boolean;
  onChoosePlan: (planId: string) => void;
  onContinue: () => void;
  onRetry: () => void;
};

export const SignupPlanView: React.FC<Props> = ({
  plans,
  localNumberPriceUsd,
  loading,
  error,
  notice,
  inProgress,
  startingPlanId,
  unavailable,
  onChoosePlan,
  onContinue,
  onRetry,
}) => {
  // Before the spinner: choosing a plan starts a second signup beside the one that may still be sitting there.
  if (unavailable) {
    return <SignupStepUnavailable onRetry={onRetry} />;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {notice != null && (
        <Alert severity="warning" sx={noticeSx}>
          {NOTICE_TEXT[notice]}
        </Alert>
      )}

      {/*
        Offered rather than jumped into: sent straight back to the step they stopped on, a returning client
        had no way off it, and the plans underneath are what makes leaving it possible.
      */}
      {inProgress != null && (
        <Alert
          severity="info"
          sx={noticeSx}
          action={
            <Button size="small" variant="contained" onClick={onContinue}>
              Continue
            </Button>
          }
        >
          {`${IN_PROGRESS_TEXT[inProgress]}${START_OVER_TEXT}`}
        </Alert>
      )}

      <Typography variant="h4" fontWeight="fontWeightSemiBold" gutterBottom>
        Choose your plan
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Every plan includes a local phone number. You can change your plan later.
      </Typography>

      {error != null && (
        <Alert severity="error" sx={noticeSx}>
          {error}
        </Alert>
      )}

      {plans.length === 0 && error == null && (
        <Alert severity="info">Self-service signup is not available right now.</Alert>
      )}

      <Grid container spacing={3}>
        {plans.map(plan => (
          <Grid item xs={12} sm={6} md={4} key={plan.id}>
            <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight="fontWeightSemiBold">
                  {plan.name}
                </Typography>
                <Typography variant="h4" fontWeight="fontWeightBold" mt={1}>
                  {usd.format(plan.price)}
                  <Typography component="span" variant="body2" color="text.secondary">
                    {` / ${plan.duration > 1 ? `${plan.duration} ` : ''}${plan.period}${plan.duration > 1 ? 's' : ''}`}
                  </Typography>
                </Typography>

                <Stack spacing={0.5} mt={2}>
                  <Typography variant="body2">{plan.credits.toLocaleString()} credits included</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {usd.format(plan.pricePerOverageCredit)} per extra credit
                  </Typography>
                  <Typography variant="body2" data-testid="signup-number-price">
                    Local phone number{' '}
                    {/* Zero is what an application that never set a price reads as, and a struck-through
                        $0.00 next to "included" reads as a defect rather than a saving. */}
                    {localNumberPriceUsd > 0 && (
                      <>
                        <Typography
                          component="s"
                          color="text.secondary"
                        >
                          {usd.format(localNumberPriceUsd)}
                        </Typography>{' '}
                      </>
                    )}
                    <Typography component="span" fontWeight="fontWeightSemiBold">
                      included
                    </Typography>
                  </Typography>
                </Stack>
              </CardContent>

              <Box p={2} pt={0}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={startingPlanId != null}
                  onClick={() => onChoosePlan(plan.id)}
                >
                  {startingPlanId === plan.id ? 'Starting…' : `Choose ${plan.name}`}
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
